import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './BillingPage.css';
import { useI18n } from './i18n';
import { isDafOneEmbedded } from './lib/embedMode';

const BillingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const clientInfo = location.state?.clientInfo || { order_cost: 0.02, order_cost_no_ai: 0.02, order_cost_ai: 0.06 }; // default order_cost (without AI)
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shopifyPlans, setShopifyPlans] = useState([]);
  const { t, lang } = useI18n();
  const currencyLabel = t('currency.egp');

  // 🔥 تحديد الباقات حسب Platform Type
  const platformType = clientInfo?.platform_type || clientInfo?.platform || 'easyorder';
  const isShopify = platformType.toLowerCase() === 'shopify';

  // 🔥 باقات Shopify
  const shopifyPackages = [
    {
      id: 1,
      name: 'Starter',
      priceUSD: 6,
      priceEGP: 300,
      orders: 100,
      orderCost: 0.06,
      orderCostEGP: 3,
      discount: 0,
      popular: false
    },
    {
      id: 2,
      name: 'Basic',
      priceUSD: 18,
      priceEGP: 900,
      orders: 300,
      orderCost: 0.06,
      orderCostEGP: 3,
      discount: 0,
      popular: true // الأكثر شعبية
    },
    {
      id: 3,
      name: 'Professional',
      priceUSD: 30,
      priceEGP: 1500,
      orders: 500,
      orderCost: 0.06,
      orderCostEGP: 3,
      discount: 0,
      popular: false
    },
    {
      id: 4,
      name: 'Enterprise',
      priceUSD: 100,
      priceEGP: 5000,
      orders: 'غير محدود',
      orderCost: null,
      popular: false,
      isUnlimited: true,
      duration: 'Monthly'
    }
  ];

  // 🔥 باقات EasyOrder (الباقات الأصلية)
  const easyOrderPackages = [
    {
      id: 1,
      name: 'Starter',
      priceUSD: 6,
      priceEGP: 300,
      orders: 100,
      orderCost: 0.06,
      orderCostEGP: 3,
      discount: 0,
      popular: false
    },
    {
      id: 2,
      name: 'Basic',
      priceUSD: 18,
      priceEGP: 900,
      orders: 300,
      orderCost: 0.06,
      orderCostEGP: 3,
      discount: 0,
      popular: true // الأكثر شعبية
    },
    {
      id: 3,
      name: 'Professional',
      priceUSD: 30,
      priceEGP: 1500,
      orders: 500,
      orderCost: 0.06,
      orderCostEGP: 3,
      discount: 0,
      popular: false
    },
    {
      id: 4,
      name: 'Unlimited Orders',
      priceUSD: 100,
      priceEGP: 5000,
      orders: 'غير محدود',
      orderCost: null,
      popular: false,
      isUnlimited: true,
      duration: 'Monthly'
    }
  ];

  // اختيار الباقات حسب النوع مع ضبط الترتيب حسب اللغة
  const basePackages = isShopify ? shopifyPackages : easyOrderPackages;
  const packages = lang === 'en' ? [...basePackages].reverse() : basePackages;

  // 🔥 Fetch Shopify plans from API if Shopify client
  useEffect(() => {
    if (isShopify && clientInfo.shopify_domain) {
      fetchShopifyPlans();
    }
  }, [isShopify, clientInfo.shopify_domain]);

  const fetchShopifyPlans = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://confirming-app.dafagents.com/api/billing/plans?shop=${clientInfo.shopify_domain}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }

      const data = await response.json();
      
      if (data.success && data.plans) {
        setShopifyPlans(data.plans);
      }
    } catch (error) {
      console.error('Error fetching Shopify plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShopifyPurchase = async (planId) => {
    try {
      if (!clientInfo.shopify_domain || !clientInfo.shopify_access_token) {
        alert('Shopify credentials missing. Please reopen from Shopify to continue.');
        return;
      }

      setLoading(true);
      
      const response = await fetch('https://confirming-app.dafagents.com/api/billing/purchase', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          planId,
          shop: clientInfo.shopify_domain,
          accessToken: clientInfo.shopify_access_token
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create purchase: ${errorText || response.status}`);
      }

      const data = await response.json();
      
      // ✅ Redirect to Shopify billing page
      if (data.confirmationUrl) {
        console.log('Redirecting to Shopify billing:', data.confirmationUrl);
        window.location.href = data.confirmationUrl;
      } else {
        throw new Error('No confirmation URL received');
      }
    } catch (error) {
      console.error('Error purchasing plan:', error);
      alert('Failed to process purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = (pkg) => {
    const brandName = clientInfo.brand_name || clientInfo.brandName || (lang === 'en' ? 'New Client' : 'عميل جديد');
    const planName = pkg.name || (pkg.isUnlimited ? 'Unlimited Orders' : `${pkg.orders} Orders`);

    let message;
    if (lang === 'en') {
      const ordersText = pkg.isUnlimited ? 'Unlimited Orders ♾️ (Monthly)' : `${pkg.orders} Orders`;
      const priceText = pkg.isUnlimited ? `$${pkg.priceUSD} / month` : `$${pkg.priceUSD} one-time`;
      message = `Hello 👋

🏪 Brand Name: ${brandName}

I'd like to subscribe to the ${planName} plan

💰 Price: ${priceText}
📦 Orders: ${ordersText}`;
    } else {
      const ordersText = pkg.isUnlimited ? 'أوردرات غير محدودة ♾️ (شهرياً)' : `${pkg.orders} أوردر`;
      const priceText = pkg.isUnlimited ? `${pkg.priceUSD}$ / شهر` : `${pkg.priceUSD}$ دفعة واحدة`;
      message = `السلام عليكم 👋

🏪 اسم البراند: ${brandName}

عايز باقة ${planName}

💰 السعر: ${priceText}
📦 عدد الأوردرات: ${ordersText}`;
    }

    const whatsappUrl = `https://wa.me/201558329566?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const showSteps = !isShopify; // hide steps block for Shopify

  // 🔗 Managed billing bridge (Daf One): defense-in-depth guard. Renders the
  // "managed by Daf One" screen instead of the plan grid when (a) the brand is
  // flagged billing_managed_by === 'daf_one', OR (b) the dashboard is embedded
  // inside Daf One (?embedded=1). Independent merchants in a normal tab (neither)
  // are never affected.
  const isBillingManagedByDafOne =
    clientInfo?.billing_managed_by === 'daf_one' || isDafOneEmbedded();
  if (isBillingManagedByDafOne) {
    return (
      <div className="billing-container" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div className="billing-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            {t('billing.back')}
          </button>
          <h1 className="billing-title">🔗 {t('billing.managedByDafOne.title')}</h1>
          <p className="billing-subtitle">{t('billing.managedByDafOne.message')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-container" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="billing-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          {t('billing.back')}
        </button>
        <h1 className="billing-title">📦 {t('billing.title')}</h1>
        <p className="billing-subtitle">{t('billing.subtitle')}</p>
      </div>

      <div className="packages-grid">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className={`package-card ${pkg.popular ? 'popular' : ''} ${
              selectedPackage?.id === pkg.id ? 'selected' : ''
            }`}
            onClick={() => setSelectedPackage(pkg)}
          >
            {pkg.popular && <div className="popular-badge">{t('billing.popular')} ⭐</div>}

            {isShopify && pkg.name && (
              <div className="package-name">{pkg.name}</div>
            )}
            
            <div className="package-header">
              <h3 className="package-orders">{pkg.isUnlimited ? '∞' : pkg.orders}</h3>
              <p className="package-label">{pkg.isUnlimited ? t('billing.unlimitedMonthly') : t('billing.ordersLabel')}</p>
            </div>

            <div className="package-pricing">
              <div className="price-usd">${pkg.priceUSD.toFixed(2)}</div>
              <div className="price-egp">{pkg.priceEGP} {currencyLabel}</div>
              {!pkg.isUnlimited && (
                <div style={{fontSize: '0.9em', color: '#00c2cb', marginTop: '8px', fontWeight: '600'}}>
                  {t('billing.perOrder')}: {pkg.orderCostEGP} {currencyLabel}
                </div>
              )}
            </div>

            <div className="package-features">
              <div className="feature">✅ {pkg.isUnlimited ? t('billing.feature.unlimitedOrders') : t('billing.feature.confirmedOrders').replace('{count}', pkg.orders)}</div>
              {!pkg.isUnlimited && pkg.discount > 0 && (
                <div className="feature" style={{color: '#10b981', fontWeight: 'bold'}}>🎉 {t('billing.feature.savePercent').replace('{discount}', pkg.discount)}</div>
              )}
              <div className="feature">✅ {t('billing.feature.instantConfirm')}</div>
              <div className="feature">✅ {t('billing.feature.fastActivation')}</div>
            </div>

            <button
              className="whatsapp-button"
              onClick={(e) => {
                e.stopPropagation();
                if (isShopify) {
                  handleShopifyPurchase(pkg.id);
                } else {
                  handleWhatsAppClick(pkg);
                }
              }}
            >
              {isShopify ? (
                <>
                  <span className="whatsapp-icon">💳</span>
                  {t('billing.buyNow')}
                </>
              ) : (
                <>
                  <span className="whatsapp-icon">💬</span>
                  {t('billing.buyWhatsApp')}
                </>
              )}
            </button>

            <div className="cost-per-order">
              {pkg.isUnlimited 
                ? `${t('billing.monthly')} ♾️` 
                : `${t('billing.perOrder')}: $${pkg.orderCost.toFixed(2)} / order`
              }
            </div>
          </div>
        ))}
      </div>

      {showSteps && (
        <div className="payment-info">
          <h2>📞 {t('billing.steps.title')}</h2>
          <div className="info-steps">
            <div className="step">
              <span className="step-number">1</span>
              <p>{t('billing.steps.1')}</p>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <p>{t('billing.steps.2')}</p>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <p>{t('billing.steps.3')}</p>
            </div>
            <div className="step">
              <span className="step-number">4</span>
              <p>{t('billing.steps.4')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
